import { useEffect, useState } from "react";
import { api, fishBadge } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Package, Check, ExternalLink, ShoppingCart, Store, Star } from "lucide-react";

const ALL = "__all__";

export default function KitsPage() {
  const [kits, setKits] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [active, setActive] = useState(null);
  const [store, setStore] = useState(ALL);

  useEffect(() => {
    Promise.all([api.get("/kits"), api.get("/retailers")]).then(([k, r]) => {
      setKits(k.data);
      setRetailers(r.data);
      setActive(k.data[0]?.id);
    });
  }, []);

  const retailerOf = (id) => retailers.find((r) => r.id === id) || {};
  const searchLink = (r, q) => (r.search_url ? r.search_url.replace("{q}", encodeURIComponent(q)) : r.url);
  const visible = store === ALL ? kits : kits.filter((k) => k.retailers.includes(store));

  const pickStore = (id) => {
    setStore(id);
    const list = id === ALL ? kits : kits.filter((k) => k.retailers.includes(id));
    if (!list.some((k) => k.id === active)) setActive(list[0]?.id);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700/80 flex items-center gap-2">
          <Package className="w-4 h-4" /> Préparez votre sortie
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 mt-2">
          Kits d'équipement recommandés
        </h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Des sélections prêtes à l'emploi selon votre type de pêche, chez Decathlon (Caperlan), Intersport,
          E.Leclerc, Pacific Pêche ou Pecheur.com. Comparez, choisissez votre enseigne et achetez.
        </p>
      </div>

      {/* retailer filter */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5 mr-1">
          <Store className="w-4 h-4" /> Filtrer par enseigne :
        </span>
        <button
          type="button"
          onClick={() => pickStore(ALL)}
          data-testid="store-filter-all"
          className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${store === ALL ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-emerald-800 border-emerald-900/15 hover:bg-emerald-50"}`}
        >
          Toutes
        </button>
        {retailers.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => pickStore(r.id)}
            data-testid={`store-filter-${r.id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors"
            style={store === r.id ? { background: r.color, borderColor: r.color, color: "#fff" } : { borderColor: `${r.color}40`, color: r.color, background: "#fff" }}
          >
            {r.name}
            {r.primary && <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] px-1.5 py-0">Reco</Badge>}
          </button>
        ))}
      </div>

      {/* kit tabs */}
      <Tabs value={active} onValueChange={setActive} className="mt-8">
        <TabsList className="flex flex-wrap h-auto bg-emerald-50 p-1.5 rounded-2xl gap-1">
          {visible.map((k) => (
            <TabsTrigger
              key={k.id}
              value={k.id}
              data-testid={`kit-tab-${k.id}`}
              className="rounded-xl data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-4 py-2 text-sm font-semibold"
            >
              {k.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {visible.map((k) => (
          <TabsContent key={k.id} value={k.id} className="mt-6">
            <div className="grid lg:grid-cols-[380px_1fr] gap-8">
              {/* kit summary */}
              <div className="rounded-3xl overflow-hidden bg-white border border-emerald-900/10 self-start">
                <div className="relative h-52">
                  <img src={k.image} alt={k.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 mb-1">{k.level}</Badge>
                    <h2 className="font-display text-2xl font-bold text-white">{k.title}</h2>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-600">{k.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {k.target_fish.map((f) => (
                      <span key={f} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${fishBadge(f)}`}>{f}</span>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center justify-between rounded-2xl bg-emerald-50 p-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-emerald-700/70">Budget estimé</p>
                      <p className="font-display text-2xl font-extrabold text-emerald-900">≈ {k.total_estimate} €</p>
                    </div>
                    <ShoppingCart className="w-8 h-8 text-emerald-500" />
                  </div>
                </div>
              </div>

              {/* checklist */}
              <div className="rounded-3xl bg-white border border-emerald-900/10 p-6">
                <h3 className="font-display text-lg font-bold text-emerald-950 mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" /> Liste de matériel
                </h3>
                <div className="divide-y divide-emerald-900/5">
                  {k.items.map((item, i) => {
                    const r = retailerOf(item.retailer);
                    return (
                      <div key={i} className="flex items-center gap-4 py-3.5" data-testid={`kit-item-${k.id}-${i}`}>
                        <span className={`grid place-items-center w-8 h-8 rounded-full shrink-0 ${item.essential ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700"}`}>
                          {item.essential ? <Star className="w-4 h-4 fill-white" /> : <Check className="w-4 h-4" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-emerald-950 text-sm leading-tight">{item.name}</p>
                          <p className="text-xs text-slate-500">{item.brand} · <span style={{ color: r.color }}>{r.name}</span></p>
                        </div>
                        <span className="font-mono font-bold text-emerald-900 text-sm">{item.price.toFixed(2)} €</span>
                        <a
                          href={searchLink(r, item.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`buy-item-${k.id}-${i}`}
                          className="grid place-items-center w-9 h-9 rounded-full bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-700 transition-colors shrink-0"
                          title={`Acheter chez ${r.name}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
                  <Star className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" /> Matériel essentiel
                </div>
                <p className="mt-6 text-sm font-semibold text-emerald-950 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-emerald-600" /> Où acheter ce kit ?
                </p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid={`kit-retailers-${k.id}`}>
                  {k.retailers.map((rid) => {
                    const r = retailerOf(rid);
                    return (
                      <a
                        key={rid}
                        href={searchLink(r, k.fishing_type)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`kit-buy-${k.id}-${rid}`}
                        className="flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-full transition-transform duration-200 hover:-translate-y-0.5"
                        style={{ background: r.color }}
                      >
                        Acheter chez {r.name} <ExternalLink className="w-4 h-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
